import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { api, download, errMsg, fmtNum } from "../lib/api";
import { PageHeader, Section, StatusBadge, statusLabel } from "../components/common";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download } from "lucide-react";

const ALL = "__all";

export default function Analytics() {
  const [cid, setCid] = useState(ALL);
  const [days, setDays] = useState("30");
  const [camps, setCamps] = useState([]);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/campaigns").then((r) => setCamps(r.data)); }, []);
  useEffect(() => { api.get("/analytics", { params: { campaign_id: cid === ALL ? "" : cid, days } }).then((r) => setD(r.data)); }, [cid, days]);
  const exp = async () => { try { await download(`/analytics/export?campaign_id=${cid === ALL ? "" : cid}`, "campus-analytics.csv"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow="Performance" title="Analyses" description="Les ouvertures sont des estimations (pixels bloqués ou préchargés par certains clients mail) et ne prouvent pas une lecture."
        actions={<Button variant="outline" onClick={exp} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" />Exporter CSV</Button>} />
      <div className="flex gap-2 mb-6">
        <Select value={cid} onValueChange={setCid}><SelectTrigger className="w-64" data-testid="analytics-campaign-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Toutes les campagnes</SelectItem>{camps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={days} onValueChange={setDays}><SelectTrigger className="w-36" data-testid="analytics-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["7", "30", "90"].map((x) => <SelectItem key={x} value={x}>{x} jours</SelectItem>)}</SelectContent></Select>
      </div>
      {d && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2"><Section title="Activité quotidienne">
              <div className="h-64" data-testid="analytics-line-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.series}>
                    <CartesianGrid stroke="#eef1f5" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={30} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line dataKey="sent" name="Envoyés" stroke="#0b1f3a" strokeWidth={2} dot={false} />
                    <Line dataKey="replies" name="Réponses" stroke="#3b82c4" strokeWidth={2} dot={false} />
                    <Line dataKey="bounces" name="Rebonds" stroke="#c2410c" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section></div>
            <Section title="Classification des réponses">
              {d.categories.length === 0 ? <div className="h-64 grid place-items-center text-sm text-slate-500" data-testid="analytics-bar-empty">Aucune réponse sur la période.</div> : (
              <div className="h-64" data-testid="analytics-bar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.categories.map((c) => ({ ...c, label: statusLabel(c.category) }))} layout="vertical">
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0b1f3a" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              )}
            </Section>
          </div>
          <div className="border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-table">
              <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>{["Campagne", "Statut", "Contacts atteints", "Emails envoyés", "Ouvertures (estim.)", "Clics", "Réponses", "Taux de réponse", "Rebonds", "Désinscriptions"].map((h) => <th key={h} className="px-4 py-2.5 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {d.campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium">{c.name}</td><td className="px-4"><StatusBadge status={c.status} /></td>
                    <td className="px-4 tabular">{fmtNum(c.contacts_reached)}</td><td className="px-4 tabular">{fmtNum(c.emails_sent)}</td>
                    <td className="px-4 tabular">{fmtNum(c.opened_est)} <span className="text-slate-400">(~{c.open_rate_est} %)</span></td>
                    <td className="px-4 tabular">{fmtNum(c.clicked)} <span className="text-slate-400">({c.click_rate} %)</span></td>
                    <td className="px-4 tabular">{fmtNum(c.replies)}</td><td className="px-4 tabular">{c.reply_rate} %</td>
                    <td className="px-4 tabular">{fmtNum(c.bounces)} <span className="text-slate-400">({c.bounce_rate} %)</span></td>
                    <td className="px-4 tabular">{fmtNum(c.unsubscribes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
